import { Component, inject, OnInit, signal, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { TooltipDirective } from 'src/app/shared/directives/tooltip/tooltip.directive';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';

import { NgClass } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import { IWebConfig } from 'src/app/shared/model/IWebConfig';
import { AuthService } from 'src/app/shared/services/auth.service';
import { LayoutService } from 'src/app/shared/services/layout.service';
import { Menu, NavService } from 'src/app/shared/services/nav.service';
import { ImagesService } from '../../services/images/images.service';
import { WebConfigService } from '../../services/web-config/web-config.service';
import { FeatherIconsComponent } from '../feather-icons/feather-icons.component';
import { AccountSidebarComponent } from './account/account-sidebar.component';
import { FeatureToggleService } from '../../services/feature-toggle/feature-toggle.service';
import { Feature } from '../../model/features.enum';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [TooltipDirective, NgClass, RouterLink, TranslatePipe, FeatherIconsComponent, AccountSidebarComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class SidebarComponent implements OnInit {
  webconfig: IWebConfig = inject(WebConfigService);
  #imagesService = inject(ImagesService);
  #featureService = inject(FeatureToggleService);
  /**
   * The sidebar's look, by element. `collapsed:` is the icon-only rail (`.sidebar-rail` on the wrapper, see
   * tailwind.css); `group/link` marks a top-level link so its icon, label and chevron can follow its hover/active state.
   */
  protected readonly ui = {
    logoWrapper:
      'px-[30px] py-[26px] shadow-[-9px_0_20px_rgba(89,102,122,0.1)] max-lg:flex max-lg:justify-between max-lg:py-[17px] collapsed:hidden',
    logoImg: 'w-[150px] max-w-full h-auto',
    toggle:
      'absolute top-[30px] right-[30px] cursor-pointer btn-bare text-start text-(--sidebar-toggle-icon-color) max-lg:hidden [&_svg]:w-5 [&_svg]:align-middle',
    logoIconWrapper:
      'hidden cursor-pointer border-0 bg-transparent px-[30px] py-[26px] shadow-[-9px_0_20px_rgba(89,102,122,0.1)] collapsed:block collapsed:w-full collapsed:text-right',
    logoIconImg: 'w-8 max-w-full h-auto',
    links:
      'mt-5 mb-[30px] h-[calc(100vh-115px)] max-lg:h-[calc(100vh-95px)] collapsed:h-[calc(100vh-118px)] collapsed:max-lg:h-[unset] ' +
      'overflow-auto text-transparent transition-[color] duration-1000 ease-css hover:text-[rgba(68,102,242,0.1)] ' +
      '[&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar]:rounded-[13px] [&::-webkit-scrollbar-thumb]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-[13px] ' +
      '[&::-webkit-scrollbar-thumb]:bg-white [&::-webkit-scrollbar-thumb]:[box-shadow:inset_0_0_0_10px] [&::-webkit-scrollbar-track]:[-webkit-box-shadow:inset_0_0_6px_transparent]',
    item: 'relative block w-full px-5 max-[1460px]:mr-2 collapsed:m-0 collapsed:max-w-[86px] collapsed:transition-all collapsed:duration-300 collapsed:ease-css',
    link:
      'group/link relative block rounded-(--sidebar-btn-border-radius) px-[15px] py-3 font-medium text-(--sidebar-font-color) ' +
      'transition-all duration-500 ease-css collapsed:px-3.5 collapsed:duration-300 hover:bg-(--sidebar-link-hover-color) hover:duration-300 ' +
      '[&.active]:mb-2.5 [&.active]:bg-(--sidebar-selected-background-color) [&.active]:[background-image:var(--sidebar-selected-background-color)] [&.active]:duration-500 [&.active:hover]:bg-(--sidebar-link-hover-color) [&.active:hover]:duration-300',
    /** A `<button>` standing in for a link: full width, no browser chrome, the surrounding text styles. */
    buttonReset:
      'w-full cursor-pointer border-0 bg-transparent text-left [font-family:inherit] [font-size:inherit] [letter-spacing:1px]',
    icon:
      '[&_svg]:mr-2.5 [&_svg]:w-[18px] [&_svg]:align-bottom [&_svg]:[stroke-width:2.3px] [&_svg]:text-(--sidebar-icon-color) ' +
      '[&_svg]:transition-all [&_svg]:duration-300 [&_svg]:ease-css ' +
      'group-[:not(.active):hover]/link:[&_svg]:fill-(--sidebar-icon-hover-color) group-[:not(.active):hover]/link:[&_svg]:stroke-white ' +
      'group-[.active]/link:[&_svg]:text-(--sidebar-selected-icon-color)',
    label:
      'font-[Roboto,sans-serif] tracking-[0.7px] capitalize collapsed:hidden ' +
      'group-[:not(.active):hover]/link:text-white group-[:not(.active):hover]/link:transition-all group-[:not(.active):hover]/link:duration-300 group-[:not(.active):hover]/link:ease-css ' +
      'group-[.active]/link:text-(--sidebar-selected-text-color) group-[.active]/link:transition-all group-[.active]/link:duration-300 group-[.active]/link:ease-css',
    accordion: 'absolute top-[17px] right-[15px] collapsed:hidden',
    accordionIcon:
      'align-middle font-semibold text-[rgba(56,77,72,0.8)] group-[.active]/link:text-white ' +
      'group-[:not(.active):hover]/link:text-white group-[:not(.active):hover]/link:transition-all group-[:not(.active):hover]/link:duration-300 group-[:not(.active):hover]/link:ease-css',
    badge:
      'absolute top-[14.5px] right-[50px] z-1 px-[0.7em] pt-[0.44em] pb-[0.32em] collapsed:w-0 collapsed:opacity-0',
    subMenu: 'relative w-full p-0',
    subLink:
      'relative block py-[7px] pr-[25px] pl-10 font-[Roboto] text-[14px] font-medium tracking-[0.06em] text-(--dark-color) ' +
      'transition-all duration-300 ease-css hover:text-primary [&.active]:text-primary collapsed:hidden ' +
      "after:absolute after:top-[48%] after:left-5 after:w-1.5 after:border-t-2 after:border-[#595c60] after:content-[''] " +
      'hover:after:border-primary [&.active]:after:border-primary',
    subLabel: 'font-[Roboto,sans-serif] tracking-[0.7px] capitalize',
    subAccordion: 'absolute top-2.5 right-[15px] collapsed:hidden',
    subAccordionIcon: 'align-middle font-semibold text-[rgba(56,77,72,0.8)]',
    childMenu: 'relative right-0 w-full p-0',
    childLink:
      'relative block py-1.5 pr-[45px] pl-14 font-[Roboto] text-[14px] font-medium tracking-[0.06em] text-(--dark-color) ' +
      'transition-all duration-300 ease-css hover:text-primary [&.active]:text-primary collapsed:hidden after:hidden',
  } as const;

  iconSidebar: any;
  menuItems = signal<Menu[]>([]);
  collapseSidebar = signal<boolean>(false);
  url: any;
  fileurl: any;
  orgId: string;
  portalId: string;
  logo = signal<string>('');
  icon = signal<string>('');
  newTheme = signal<boolean>(this.#featureService.when(Feature.NewTheme)() as boolean);

  constructor(
    private router: Router,
    public navServices: NavService,
    public layout: LayoutService,
    public authService: AuthService,
  ) {
    this.authService.user.subscribe(() => {
      this.initiateMenuItems();
    });
  }

  ngOnInit() {
    this.currentActiveRoute();
    this.orgId = this.webconfig.orgId;
    this.portalId = this.webconfig.portalId;
    this.#setLogo();
  }

  sidebarToggle() {
    this.navServices.collapseSidebar = !this.navServices.collapseSidebar;
    this.collapseSidebar.set(this.navServices.collapseSidebar);
  }

  #setLogo(): void {
    this.logo.set(this.webconfig.assets.logo);
    this.icon.set(this.webconfig.assets.icon);
    this.#imagesService.checkImageExistsHead(this.webconfig.assets.logo).then((exists) => {
      if (!exists) {
        this.logo.set(ImagesService.logos.logo);
        this.icon.set(ImagesService.logos.icon);
      }
    });
  }

  currentActiveRoute(): void {
    this.menuItems().forEach((item) => {
      if (item.path === this.router.url) {
        item.active = true;
      }
    });
  }

  // Active Nav state
  setNavActive(item: Menu) {
    const items = this.menuItems();
    items.forEach((menuItem) => {
      if (menuItem !== item) {
        menuItem.active = false;
      }
      if (menuItem.children && menuItem.children.includes(item)) {
        menuItem.active = true;
      }
      if (menuItem.children) {
        menuItem.children.forEach((submenuItems) => {
          if (submenuItems.children && submenuItems.children.includes(item)) {
            menuItem.active = true;
            submenuItems.active = true;
          }
        });
      }
    });
  }

  // Click Toggle menu
  toggledNavActive(item: Menu) {
    let found = false;
    const items = this.menuItems();
    items.forEach((a) => {
      if (a === item) {
        found = true;
        a.active = !a.active;
      } else {
        a.active = false;
        if (!a.children) {
          return;
        }
        a.children.forEach((b) => {
          if (b === item) {
            found = true;
            b.active = !b.active;
            a.active = true;
          } else {
            b.active = false;
          }
        });
      }
    });

    if (!found) {
      item.active = true;
    }
  }

  initiateMenuItems() {
    const items: BehaviorSubject<Menu[]> = this.navServices.menuItems;

    items.subscribe((menuItems) => {
      this.menuItems.set(menuItems);
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          menuItems.forEach((items) => {
            if (items.path === event.url) {
              this.setNavActive(items);
            }
            if (!items.children) {
              return;
            }
            items.children.forEach(this.forEachChildren.bind(this, event));
          });
        }
      });
    });
  }

  forEachChildren(event: NavigationEnd, subItems: Menu) {
    if (subItems.path === event.url) {
      this.setNavActive(subItems);
    }
    if (!subItems.children) {
      return;
    }
    subItems.children.forEach((subSubItems) => {
      if (subSubItems.path === event.url) {
        this.setNavActive(subSubItems);
      }
    });
  }
}
